// Small pure helpers for the chat panel: day separators, what's typed into the link box, a file's badge, the
// formatting shortcuts and the photos of a thread (for the lightbox). No React here, so they're easy to test.
import { chatFileKind, safeChatHref, type ChatAttachment } from '@technohull/shared';
import { localTodayIso, type DateFormatter } from '../lib/dates.js';

/** the local calendar day of a moment ('YYYY-MM-DD'): messages are grouped under a separator per day. */
export const chatDayKey = (ms: number): string => localTodayIso(new Date(ms));

/** a day separator: "Today", "Yesterday", else the date in the user's own format, with its weekday. */
export function chatDayLabel(ms: number, df: DateFormatter, now: Date = new Date()): string {
  const key = chatDayKey(ms);
  if (key === localTodayIso(now)) return 'Today';
  const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 12);
  if (key === localTodayIso(yesterday)) return 'Yesterday';
  return df.date(ms, { year: 'auto', weekday: 'short', now });
}

/** what someone typed into the link box, as a link the chat keeps: "technohull.com/x" → https://…,
 *  "name@technohull.com" → mailto:…. null when it is not a web or mail address (javascript: and the like). */
export function normalizeLinkInput(raw: string): string | null {
  const s = raw.trim();
  if (!s) return null;
  if (/^(?:https?:\/\/|mailto:)/i.test(s)) return safeChatHref(s);
  if (/^[^\s@/:]+@[^\s@/:]+\.[^\s@/:]+$/.test(s)) return safeChatHref(`mailto:${s}`);
  // another scheme (but "host:8080" is a port, not a scheme)
  if (/^[a-z][a-z0-9+.-]*:/i.test(s) && !/^[^:/]+:\d/.test(s)) return null;
  if (/^(?:www\.)?[\p{L}\p{N}-]+(?:\.[\p{L}\p{N}-]+)+(?::\d+)?(?:[/?#]\S*)?$/u.test(s)) return safeChatHref(`https://${s}`);
  return null;
}

export type FileTone = 'pdf' | 'doc' | 'sheet' | 'slides' | 'image' | 'archive' | 'media' | 'file';
const TONES: Array<[RegExp, FileTone]> = [
  [/^pdf$/, 'pdf'],
  [/^(?:docx?|odt|rtf|txt|md|pages)$/, 'doc'],
  [/^(?:xlsx?|xlsm|ods|csv|numbers)$/, 'sheet'],
  [/^(?:pptx?|odp|key)$/, 'slides'],
  [/^(?:jpe?g|png|gif|webp|avif|heic|heif|svg|bmp|tiff?|dwg|dxf)$/, 'image'],
  [/^(?:zip|rar|7z|gz|tgz|tar)$/, 'archive'],
  [/^(?:mp4|mov|m4v|avi|mkv|webm|mp3|m4a|wav|ogg)$/, 'media'],
];
/** a file card's badge: its extension ("PDF", "XLSX") and a colour family for it. */
export function fileBadge(a: Pick<ChatAttachment, 'name' | 'type'>): { label: string; tone: FileTone } {
  const ext = /\.([a-z0-9]{1,5})$/i.exec(a.name)?.[1]?.toLowerCase() ?? '';
  // no extension: a type that names its format ("image/png"), else just a file
  const t = a.type.toLowerCase();
  const key = ext || (t === 'application/pdf' ? 'pdf' : t === 'text/plain' ? 'txt' : /^(?:image|video|audio)\/([a-z0-9]{2,5})$/.exec(t)?.[1] ?? '');
  const tone = TONES.find(([re]) => re.test(key))?.[1] ?? (a.type.startsWith('image/') ? 'image' : a.type.startsWith('video/') || a.type.startsWith('audio/') ? 'media' : 'file');
  return { label: (key || 'file').slice(0, 4).toUpperCase(), tone };
}

export type FormatCommand = 'bold' | 'italic' | 'underline' | 'strike' | 'code' | 'link' | 'ul' | 'ol' | 'quote';
/** the formatting a key press asks for (Ctrl on Windows, ⌘ on a Mac): B / I / U, K for a link, E for code,
 *  and with Shift: X strikethrough, 7 numbered list, 8 bulleted list, 9 quote. */
export function shortcutFor(e: { key: string; code?: string; ctrlKey: boolean; metaKey: boolean; shiftKey: boolean; altKey: boolean }): FormatCommand | null {
  if (!(e.ctrlKey || e.metaKey) || e.altKey) return null;
  const k = e.key.toLowerCase();
  if (!e.shiftKey) return ({ b: 'bold', i: 'italic', u: 'underline', k: 'link', e: 'code' } as const)[k as 'b'] ?? null;
  if (k === 'x') return 'strike';
  if (e.code === 'Digit7' || k === '7' || k === '&') return 'ol';
  if (e.code === 'Digit8' || k === '8' || k === '*') return 'ul';
  if (e.code === 'Digit9' || k === '9' || k === '(') return 'quote';
  return null;
}

export interface ThreadPhoto extends ChatAttachment { from: string; at: number }
/** every photo in a thread, oldest first, with who sent it and when (the lightbox steps through them). */
export function threadPhotos(messages: ReadonlyArray<{ fromName: string; at: number; deleted: boolean; attachments: ChatAttachment[] }>): ThreadPhoto[] {
  return messages.flatMap((m) => (m.deleted ? [] : m.attachments.filter((a) => chatFileKind(a.type) === 'image').map((a) => ({ ...a, from: m.fromName, at: m.at }))));
}

/** a single photo's box in a bubble: its own aspect, kept between tall-ish and wide-ish (the rest is cropped). */
export function photoAspect(a: Pick<ChatAttachment, 'width' | 'height'>): number {
  if (!a.width || !a.height) return 4 / 3;
  return Math.min(2, Math.max(0.6, a.width / a.height));
}
